import {
  Box,
  Grid,
  GridProps,
  Radio,
  RadioGroup,
  Stack,
  StackProps,
  Text,
} from "@chakra-ui/react";
import { FieldError } from "react-hook-form";

import { SectionTitle, SectionTitleProps } from "./SectionTitle";

type SectionCardsProps<T> = GridProps &
  SectionTitleProps & {
    formWatch: T;
    nameValue: T;
    numberOfColumns?: {
      mobile: number;
      desktop: number;
    };
    setValue: (name: T, value: string) => void;
    items: {
      [key: string]: {
        translationId: string;
        id: string;
        queryValue?: string;
        description?: string;
      };
    };
    cardStackCss?: StackProps;
    error?: FieldError;
    isDisabled?: boolean;
    isTitleRow?: boolean;
  };

interface ICard<T> extends StackProps {
  id: string;
  isHighlighted: boolean;
  setValue: (name: T, value: string) => void;
  nameValue: T;
  item: {
    translationId: string;
    queryValue?: string;
    description?: string;
  };
  cardStackCss?: StackProps;
}

export const Card: React.FC<ICard<any>> = ({
  id,
  item,
  isHighlighted,
  setValue,
  nameValue,
  cardStackCss,
}) => (
  <Stack
    key={id}
    borderColor={isHighlighted ? "ud-success" : "ud-grey-300"}
    borderWidth="1px"
    borderStyle="solid"
    p="11px"
    borderRadius="6px"
    backgroundColor={isHighlighted ? "ud-green-100" : "ud-white"}
    h="40px"
    justify="center"
    onClick={() => setValue(nameValue, id)}
    _hover={{
      borderColor: "ud-success",
    }}
    spacing="2px"
    sx={{
      "> *": {
        cursor: "pointer",
      },
    }}
    {...cardStackCss}
  >
    <Radio value={id} colorScheme="radioColor">
      <Stack direction="row" ml="8px" align="center">
        <Text fontWeight="bold" color={isHighlighted ? "ud-success" : "unset"}>
          {item.translationId}
        </Text>
      </Stack>
    </Radio>
    {item.description && (
      <Text size="small" pl="32px">
        {item.description}
      </Text>
    )}
  </Stack>
);

const SectionCards: React.FC<SectionCardsProps<any>> = ({
  title,
  formWatch,
  nameValue,
  setValue,
  items,
  numberOfColumns = {
    mobile: 2,
    desktop: 3,
  },
  cardStackCss,
  error,
  isRequired,
  titlePaddingProps,
  description,
  icon,
  isTitleRow = false,
  isDisabled,
  ...props
}) => (
  <Box>
    <SectionTitle
      title={title}
      isRequired={isRequired}
      titlePaddingProps={titlePaddingProps}
      description={description}
      icon={icon}
      isTitleRow={isTitleRow}
    />
    <RadioGroup value={formWatch} isDisabled={isDisabled}>
      <Grid
        gridTemplateColumns={{
          base: `repeat(${numberOfColumns.mobile}, 1fr)`,
          sm: `repeat(${numberOfColumns.desktop}, 1fr)`,
        }}
        gap={{ base: "10px" }}
        {...props}
      >
        {Object.entries(items).map((item) => {
          const isHighlighted = item[1].id === formWatch;

          return (
            <Card
              key={item[0]}
              id={item[1].id}
              item={item[1]}
              nameValue={nameValue}
              setValue={isDisabled ? () => {} : setValue}
              isHighlighted={isHighlighted}
              cardStackCss={cardStackCss}
            />
          );
        })}
      </Grid>
    </RadioGroup>
    {error?.message && <Text variant="form-error">{error.message}</Text>}
  </Box>
);

export default SectionCards;
